import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMsal } from "@azure/msal-react";
import { callApi } from "../api/api";
import { clearAuthCaches } from "../utils/authCache";

const AccessContext = createContext(null);

export function AccessProvider({ children }) {
  const { instance, accounts } = useMsal();
  const [userId, setUserId] = useState(null);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [features, setFeatures] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inFlightRef = useRef(null);
  const loadedEmailRef = useRef(null);
  const forceWaitingRef = useRef(false);
  const accountsRef = useRef(accounts);

  accountsRef.current = accounts;

  const account = instance.getActiveAccount() || accounts?.[0] || null;
  const accountEmail = account?.username || null;

  const resetAccess = useCallback(() => {
    setUserId(null);
    setRoles([]);
    setPermissions([]);
    setFeatures([]);
    setWorkspaces([]);
    setDepartments([]);
    setIsAdmin(false);
    setError(null);
    loadedEmailRef.current = null;
  }, []);

  const loadAccess = useCallback(
    async ({ force = false } = {}) => {
      if (force) {
        forceWaitingRef.current = true;
      }

      if (!accountEmail) {
        resetAccess();
        setLoading(false);
        if (forceWaitingRef.current) {
          forceWaitingRef.current = false;
          window.dispatchEvent(new Event("portal-shell-ready"));
        }
        return null;
      }

      if (
        !force &&
        loadedEmailRef.current === accountEmail &&
        inFlightRef.current == null
      ) {
        setLoading(false);
        return null;
      }

      if (inFlightRef.current) {
        return inFlightRef.current;
      }

      const isInitialLoad = loadedEmailRef.current == null;

      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      inFlightRef.current = (async () => {
        try {
          const res = await callApi(
            instance,
            accountsRef.current,
            "GET",
            "/api/user/me",
          );
          const data = res?.data?.data || {};
          const nextPermissions = data.permissions || [];
          const adminFromPermissions = nextPermissions.some(
            (permission) => permission.resource_code === "ADMIN",
          );

          setUserId(data.user_id == null ? null : String(data.user_id));
          setRoles(data.roles || []);
          setPermissions(nextPermissions);
          setFeatures(data.portalFeatures || []);
          setWorkspaces(data.workspaces || []);
          setDepartments(data.departments || []);
          setIsAdmin(Boolean(data.is_admin) || adminFromPermissions);
          setError(null);
          loadedEmailRef.current = accountEmail;
        } catch (err) {
          console.error("Access bootstrap (/me) failed", err);
          if (isInitialLoad) {
            resetAccess();
            setError("load_failed");
          }
        } finally {
          if (isInitialLoad) {
            setLoading(false);
          }
          inFlightRef.current = null;
          if (forceWaitingRef.current) {
            forceWaitingRef.current = false;
            window.dispatchEvent(new Event("portal-shell-ready"));
          }
        }
      })();

      return inFlightRef.current;
    },
    [instance, accountEmail, resetAccess],
  );

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  useEffect(() => {
    const handleAccessChanged = () => {
      clearAuthCaches();
      loadAccess({ force: true });
    };

    window.addEventListener("rbac-changed", handleAccessChanged);

    return () => {
      window.removeEventListener("rbac-changed", handleAccessChanged);
    };
  }, [loadAccess]);

  const hasAccess = useCallback(
    (resource, access) => {
      if (!permissions?.length) return false;
      if (isAdmin) return true;

      return permissions.some(
        (permission) =>
          permission.resource_code === resource &&
          (permission.access_level_code === access ||
            permission.access_level_code === "WRITE"),
      );
    },
    [permissions, isAdmin],
  );

  const value = useMemo(
    () => ({
      userId,
      roles,
      permissions,
      features,
      workspaces,
      departments,
      isAdmin,
      loading,
      error,
      hasAccess,
      reload: () => loadAccess({ force: true }),
    }),
    [userId, roles, permissions, features, workspaces, departments, isAdmin, loading, error, hasAccess, loadAccess],
  );

  return (
    <AccessContext.Provider value={value}>{children}</AccessContext.Provider>
  );
}

export function useAccess() {
  const context = useContext(AccessContext);

  if (!context) {
    throw new Error("useAccess must be used within AccessProvider");
  }

  return context;
}
